/**
 * Create/Edit Flow Optimizer Tag Rule modal (ranking + metric conditions).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, X } from "lucide-react";
import type {
  FlowLayer,
  FlowTagCondition,
  FlowTagRule,
} from "@trafficpulse/shared";

import {
  Button,
  Card,
  ColorPicker,
  CreatableCombobox,
  Input,
  Switch,
} from "@/components/ui";
import { useModalPortalSurface } from "@/components/ui/layout/ModalPortalSurfaceContext";
import { resolveEditableRange } from "@/components/date-range";
import {
  DateRangePresetDropdown,
  getPresetFromRange,
  getRangeFromPreset,
} from "@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab";
import {
  isDefaultScope,
  useScopeLabels,
  type TagRuleScope,
} from "@/features/tag-rule-scope";
import { ScopeBanner } from "@/pages/admin/ListTagRulesPage/components/rule-condition/parts";
import { cn } from "@/lib/utils";
import { TAG_COLOR_PRESETS } from "@/pages/admin/ListTagRulesPage/constants";

import NoStatsModeCard from "./NoStatsModeCard";
import { FlowTagConditionRow } from "./FlowTagConditionRow";
import type { CreateFlowTagRuleInput } from "@/hooks";

const DEFAULT_RANKING: FlowTagCondition = {
  kind: "ranking",
  metric: "rawScore",
  op: "top_percent",
  percent: 10,
  scope: "path",
};

/** Quick-pick emojis for the tag icon (any emoji can still be typed/pasted). */
const EMOJI_PRESETS = ["🔥", "⭐", "💎", "🚀", "👑", "💰", "⚠️", "🗑️"] as const;

interface FlowTagRuleFormModalProps {
  open: boolean;
  layer: FlowLayer;
  onOpenChange: (open: boolean) => void;
  initial?: FlowTagRule | null;
  onSubmit: (dto: CreateFlowTagRuleInput) => Promise<void>;
  submitting: boolean;
  availableGroupKeys?: string[];
  /**
   * The enabled, ungrouped no-stats rule already on this layer, if any. Only
   * one may exist, so this disables the toggle and names the holder INSTEAD of
   * letting the user fill the form and hit a save-time rejection.
   */
  noStatsHolder?: FlowTagRule | null;
  /** Country/campaign in view; metric conditions edit THAT scope's override. */
  overrideScope?: TagRuleScope;
}

export const FlowTagRuleFormModal: FC<FlowTagRuleFormModalProps> = ({
  open,
  layer,
  onOpenChange,
  initial,
  onSubmit,
  submitting,
  availableGroupKeys = [],
  noStatsHolder,
  overrideScope,
}) => {
  const isEdit = !!initial;
  const { labelFor } = useScopeLabels();
  // Inside the Flow Optimizer drawer (a modal Radix dialog) this must portal
  // INTO the drawer's element — a body portal sits outside its FocusScope,
  // which reclaims focus on every click/keystroke and the inputs go dead.
  const portalHost = useModalPortalSurface();
  const defaultRange = useMemo(() => getRangeFromPreset("last-30-days"), []);
  const [name, setName] = useState("");
  const [color, setColor] = useState("");
  const [emoji, setEmoji] = useState("");
  const [conditions, setConditions] = useState<FlowTagCondition[]>([
    DEFAULT_RANKING,
  ]);
  const [fromDate, setFromDate] = useState(defaultRange?.from ?? "");
  const [toDate, setToDate] = useState(defaultRange?.to ?? "");
  const [toDateIsToday, setToDateIsToday] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [groupKey, setGroupKey] = useState("");
  const [noStats, setNoStats] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setColor(initial?.color ?? "");
    setEmoji(initial?.emoji ?? "");
    setConditions(
      initial?.conditions?.length ? [...initial.conditions] : [DEFAULT_RANKING],
    );
    setEnabled(initial?.enabled ?? true);
    setGroupKey(initial?.groupKey ?? "");
    setNoStats(initial?.appliesTo === "no-stats");
    // The stored from/to are only a snapshot of the preset at save time —
    // resolveEditableRange re-resolves a rolling preset the way the evaluator does.
    const range = resolveEditableRange(initial);
    setFromDate(range.from);
    setToDate(range.to);
    setToDateIsToday(range.toDateIsToday);
  }, [open, initial?.id, defaultRange, initial]);

  // Grouping is the one legal way to have two (groups are mutually exclusive),
  // and it is also what the backend checks — so UI and API can never disagree.
  const noStatsBlocked = !!noStatsHolder && !groupKey.trim();
  // Derived, not stored: clearing the group while the toggle is on must not
  // leave the form in a shape the backend would reject.
  const isNoStats = noStats && !noStatsBlocked;

  const update = (idx: number, c: FlowTagCondition) =>
    setConditions((prev) => prev.map((p, i) => (i === idx ? c : p)));
  const remove = (idx: number) =>
    setConditions((prev) => prev.filter((_, i) => i !== idx));
  // One ranking per rule — multiple rankings are redundant (top10 AND top10) or
  // contradictory (top% AND bottom%). Metric conditions stay unlimited.
  const hasRanking = conditions.some((c) => c.kind === "ranking");
  // The date range only feeds metric conditions; raw-score ranking uses the live
  // optimizer scores, so a ranking-only rule needs no date range.
  const hasMetric = conditions.some((c) => c.kind === "metric");
  const addRanking = () =>
    setConditions((prev) =>
      prev.some((c) => c.kind === "ranking")
        ? prev
        : [...prev, { ...DEFAULT_RANKING }],
    );
  const addMetric = () =>
    setConditions((prev) => [
      ...prev,
      { kind: "metric", field: "profit", operator: "lt", value: 0 },
    ]);

  const sanitize = (c: FlowTagCondition): FlowTagCondition => {
    if (c.kind !== "metric") return c;
    const countries = (c.countryThresholds ?? [])
      .map((t) => ({ ...t, countryKey: t.countryKey.trim().toLowerCase() }))
      .filter((t) => t.countryKey.length > 0);
    // Drop the blank draft row the picker keeps while the user chooses.
    const campaigns = (c.campaignThresholds ?? [])
      .map((t) => ({ ...t, campaignId: t.campaignId.trim() }))
      .filter((t) => t.campaignId.length > 0);
    return {
      ...c,
      countryThresholds: countries.length ? countries : undefined,
      campaignThresholds: campaigns.length ? campaigns : undefined,
    };
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    // A no-stats rule IS its population: no conditions to test, no window to
    // test them over. Sending either would be dead code the backend refuses.
    if (!isNoStats) {
      if (conditions.length === 0 || !fromDate.trim()) return;
      if (!toDateIsToday && !toDate.trim()) return;
    }
    const preset = toDateIsToday
      ? "custom"
      : getPresetFromRange(fromDate, toDate);
    await onSubmit({
      layer,
      name: name.trim(),
      color: color.trim() || undefined,
      emoji: emoji.trim() || undefined,
      conditions: isNoStats ? [] : conditions.map(sanitize),
      ...(isNoStats
        ? { appliesTo: "no-stats" as const }
        : {
            appliesTo: "scored" as const,
            dateRangePreset: preset,
            fromDate,
            toDate: toDateIsToday ? undefined : toDate,
            toDateIsToday: toDateIsToday || undefined,
          }),
      enabled,
      groupKey: groupKey.trim() || undefined,
    });
    onOpenChange(false);
  };

  if (!open) return null;

  // Portalled on purpose — inline it renders inside the drawer's transformed,
  // overflow-hidden panel and gets clipped. Target the drawer's modal surface
  // when one is published (so its FocusScope keeps the inputs typeable), else
  // <body>. `pointer-events-auto` is the second half: Radix sets
  // `pointer-events: none` on <body> while the drawer is open.
  return createPortal(
    <AnimatePresence>
      <motion.div
        className="pointer-events-auto fixed inset-0 z-[200]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => onOpenChange(false)}
          aria-hidden="true"
        />
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <Card
            className="flex max-h-[90vh] w-full max-w-2xl flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b bg-muted/30 px-6 py-4">
              <h2 className="text-lg font-semibold">
                {isEdit ? "Edit ranking tag" : "Create ranking tag"} · {layer}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
              <div>
                <label className="block text-sm font-medium">Tag name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. POTENTIAL"
                  className="mt-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">
                  Group (optional)
                </label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Same group = mutually exclusive; top (highest order) match
                  wins.
                </p>
                <CreatableCombobox
                  options={availableGroupKeys}
                  value={groupKey}
                  onChange={setGroupKey}
                  placeholder="e.g. score-tier"
                  createLabel={(v) => `Create "${v}"`}
                  className="mt-2 max-w-xs"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Tag color</label>
                <div className="mt-2 max-w-[200px]">
                  <ColorPicker
                    color={color || ""}
                    onChange={setColor}
                    placeholder="#22c55e"
                    presets={TAG_COLOR_PRESETS}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium">
                  Icon / emoji (optional)
                </label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Shown with the tag, and alone when space is tight (small
                  screens).
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input
                    value={emoji}
                    onChange={(e) => setEmoji(e.target.value)}
                    placeholder="🔥"
                    maxLength={32}
                    aria-label="Tag emoji"
                    className="w-16 text-center text-lg"
                  />
                  {EMOJI_PRESETS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setEmoji(e)}
                      aria-label={`Use ${e}`}
                      className={cn(
                        "flex h-8 w-8 items-center justify-center rounded-md border text-base transition-colors hover:bg-muted",
                        emoji === e
                          ? "border-primary bg-primary/10"
                          : "border-border",
                      )}
                    >
                      {e}
                    </button>
                  ))}
                  {emoji && (
                    <button
                      type="button"
                      onClick={() => setEmoji("")}
                      aria-label="Clear emoji"
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <NoStatsModeCard
                checked={isNoStats}
                onChange={setNoStats}
                layer={layer}
                blockedBy={noStatsBlocked ? noStatsHolder : null}
              />

              {!isNoStats && (
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium">
                      Conditions
                    </label>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      All must pass (AND). One ranking + any metric conditions.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addRanking}
                      disabled={hasRanking}
                      title={
                        hasRanking
                          ? "One ranking per rule — add metric conditions to refine it"
                          : "Add a raw-score ranking condition"
                      }
                    >
                      <Plus className="mr-1.5 h-4 w-4" /> Ranking
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addMetric}
                    >
                      <Plus className="mr-1.5 h-4 w-4" /> Metric
                    </Button>
                  </div>
                </div>
                {/* Once, above every condition — the scope is the same for all
                    of them, so repeating it per row was pure noise. */}
                {!isDefaultScope(overrideScope) && overrideScope ? (
                  <div className="mt-3">
                    <ScopeBanner label={labelFor(overrideScope)} />
                  </div>
                ) : null}
                <div className="mt-3 space-y-2">
                  {conditions.map((c, i) => (
                    <FlowTagConditionRow
                      key={i}
                      condition={c}
                      layer={layer}
                      onChange={(nc) => update(i, nc)}
                      onRemove={() => remove(i)}
                      canRemove={conditions.length > 1}
                      overrideScope={overrideScope}
                    />
                  ))}
                </div>
              </div>
              )}

              {!isNoStats && hasMetric && (
                <div>
                  <label className="block text-sm font-medium">
                    Date range (optimizer metrics)
                  </label>
                  <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
                    Used for the metric conditions; the raw-score ranking always
                    uses the live optimizer scores.
                  </p>
                  <DateRangePresetDropdown
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromChange={setFromDate}
                    onToChange={setToDate}
                    showToDateIsToday
                    toDateIsToday={toDateIsToday}
                    onToDateIsTodayChange={setToDateIsToday}
                  />
                </div>
              )}

              <div className="flex items-center gap-3 rounded-lg border border-border p-3">
                <label className="flex cursor-pointer items-center gap-2.5">
                  <Switch checked={enabled} onCheckedChange={setEnabled} />
                  <span
                    className={cn(
                      "text-sm",
                      enabled ? "font-medium" : "text-muted-foreground",
                    )}
                  >
                    {enabled ? "Rule enabled" : "Rule disabled"}
                  </span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t bg-muted/20 px-6 py-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !name.trim()}
              >
                {submitting
                  ? "Saving…"
                  : isEdit
                    ? "Save changes"
                    : "Create rule"}
              </Button>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    portalHost ?? document.body,
  );
};
